import {
  useIsMobile,
  WSAvatar,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSTabsNavigation,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo } from "react";
import {
  DataView,
  DataViewProps,
  DataViewSection
} from "../../../../shared/components/DataView";
import { selectorIsSharedFileNotNoneContext } from "../../selectors/selectorIsSharedFileNotNoneContext";
import { selectorIsSharedFileRequest } from "../../selectors/selectorIsSharedFileRequest";
import {
  ContextItem,
  RequirementContextType,
  RequirementTableCallback
} from "../../types";
import { RequirementsConfigResult } from "../../useRequirementsConfig";
import { LabelRequirementType } from "../LabelRequirementType";
import { LablePayeeName } from "../LablePayeeName";
import { LabelPayerName } from "../LablePayerName";
import { Alerts } from "../../../../shared/components/Alerts";
import { useLocationStateStorage } from "../../../../shared/hooks/useLocationStateStorage";
import { useLocation } from "react-router-dom";
import { selectorIsRequirementNotExpiring } from "../../selectors/selectorIsRequirementNotExpiring";

export type RequirementDetailsContentProps = RequirementsConfigResult & {
  contextType: RequirementContextType;
  onSuccessAction?: RequirementTableCallback;
  customContext?: ContextItem;
  requirement: IRequirementResponse;
  showTitle?: boolean;
};

export const RequirementDetailsContent: React.FC<
  RequirementDetailsContentProps
> = ({
  requirement: _requirement,
  contextType,
  isLoading,
  getRequirementName,
  getRequirementStatusAlert,
  getRequirementExpirationAlerts,
  getRequirementBasicInformation,
  getRequirementDetails,
  getRequirementDetailsWidgets,
  getRequirementDetailsProgressWidget,
  getRequirementDetailsHistoryWidget,
  getAdditionalData,
  getRequirementAdditionalContent,
  getRequirementExpirationWidget,
  getStatusCellProps,
  getTypeCellProps,
  onSuccessAction,
  showTitle = true
}) => {
  const location = useLocation();
  const [currentTab, setCurrentTab] = useLocationStateStorage(
    `requirement-details-tab-${location.pathname}`,
    "renewal"
  );

  const isRenewalAvailable = !!_requirement.replacedRequirement;
  const isEarlyRenewal =
    !!_requirement.replacedRequirement &&
    selectorIsRequirementNotExpiring(_requirement);

  const isExpiringTab = isRenewalAvailable
    ? currentTab === "expiring-soon"
    : false;

  const requirement = useMemo(() => {
    return isExpiringTab
      ? { ..._requirement, ..._requirement.replacedRequirement }
      : _requirement;
  }, [_requirement, isExpiringTab]);

  const isMobile = useIsMobile();
  const statusAlertProps = getRequirementStatusAlert(_requirement);
  const expirationAlertProps = getRequirementExpirationAlerts(_requirement);
  const renewalStatusCellProps = getStatusCellProps(_requirement);

  const requirementBasicInformation =
    getRequirementBasicInformation(
      isExpiringTab ? { ...requirement, expiresAt: undefined } : requirement
    ) || [];
  const requirementDetails = getRequirementDetails(requirement);
  const requirementDetailsWidgets = getRequirementDetailsWidgets(requirement);
  const requirementName = getRequirementName(requirement);
  const progressWidget = getRequirementDetailsProgressWidget(requirement);
  const historyWidget = getRequirementDetailsHistoryWidget(requirement);
  const expirationWidget = getRequirementExpirationWidget(requirement);
  const additionalData = getAdditionalData(requirement);
  const additionalDetailsContent = getRequirementAdditionalContent(requirement);

  return (
    <WSElement shimmer={isLoading}>
      <WSList gap="XL">
        <WSElement>
          <WSFlexBox.CenterY gap="L">
            {showTitle && <WSText.Heading2>{requirementName}</WSText.Heading2>}
            {contextType === RequirementContextType.Payee &&
            selectorIsSharedFileRequest(additionalData) &&
            selectorIsSharedFileNotNoneContext(additionalData.contextType) ? (
              <WSAvatar
                size="L"
                type="icon"
                icon="compass"
                format="square"
                colorBackground="garnet"
              />
            ) : null}
          </WSFlexBox.CenterY>

          <WSFlexBox mt="S" gap="L" alignItems="center">
            {contextType === RequirementContextType.Payee ? (
              <LablePayeeName payeeId={requirement.payeeId} />
            ) : contextType === RequirementContextType.Payer ? (
              <LabelPayerName payerId={requirement.payerId} />
            ) : null}

            <LabelRequirementType {...getTypeCellProps(requirement)} />
          </WSFlexBox>
        </WSElement>

        <Alerts alerts={[statusAlertProps, ...expirationAlertProps]} />

        {isRenewalAvailable ? (
          <WSTabsNavigation
            activeIndex={currentTab === "renewal" ? 0 : 1}
            tabs={[
              {
                label: "Renewal",
                onClick() {
                  setCurrentTab("renewal");
                },
                pill: renewalStatusCellProps.pill?.icon
                  ? {
                      icon: renewalStatusCellProps.pill.icon,
                      theme: renewalStatusCellProps.pill.theme,
                      text: undefined
                    }
                  : undefined
              },
              {
                label: isEarlyRenewal ? "Current" : "Expiring soon",
                onClick() {
                  setCurrentTab("expiring-soon");
                }
              }
            ]}
          />
        ) : null}

        <WSGrid>
          <WSGrid.Item span={{ l: "8" }}>
            <WSList gap="L">
              {isMobile ? progressWidget : null}
              <DataViewSection
                title="Requirement information"
                data={requirementBasicInformation}
                gridColumns={3}
              />
              {requirementDetails ? (
                React.isValidElement(requirementDetails) ? (
                  requirementDetails
                ) : (
                  <DataView {...(requirementDetails as DataViewProps)} />
                )
              ) : null}
              {additionalDetailsContent ? additionalDetailsContent : null}
            </WSList>
          </WSGrid.Item>
          <WSGrid.Item span={{ l: "4" }}>
            <WSList gap="L">
              {isMobile ? null : progressWidget}
              {expirationWidget}
              {requirementDetailsWidgets}
              {historyWidget}
            </WSList>
          </WSGrid.Item>
        </WSGrid>
      </WSList>
    </WSElement>
  );
};
